import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import styles from './GalleryPreview.module.sass'
import clsx from 'clsx'
import { FileData, ImageFileData, isImage } from '../../../../../responses/files'
import { urlWithParams } from '../../../../../http/utils'
import { ROUTES } from '../../../../../http/routes'
import Image from '../../../../data-components/files/Image/Image'
import { Document, Outline, Page } from 'react-pdf'
import { useQueries } from '../../../../../utils/hooks/useQueries'
import { motion, useMotionValue } from 'framer-motion'
import useMeasureDirty from 'react-use/lib/useMeasureDirty'

interface Props {
    className?: string,
    current: FileData,
}

interface Position {
    x: number,
    y: number,
}

const calculatePosition = (event: React.MouseEvent, file: ImageFileData): Position => {
    const rect = event.currentTarget.getBoundingClientRect()
    const cursorX = event.clientX - rect.left
    const cursorY = event.clientY - rect.top
    return {
        x: -cursorX / event.currentTarget.clientWidth * file.width + cursorX,
        y: -cursorY / event.currentTarget.clientHeight * file.height + cursorY,
    }
}

const GalleryPreview: FunctionComponent<Props> = props => {
    const [numPages, setNumPages] = useState(0)
    const [pageNumber, setPageNumber] = useState(1)
    const [zoom, setZoom] = useState(false)
    const [position, setPosition] = useState<Position>()

    const {isMobile} = useQueries()

    const imageRef = useRef<HTMLDivElement>(null)
    const parentRef = useRef<HTMLDivElement>(null)
    const {width, height} = useMeasureDirty(imageRef)
    const canZoom = isImage(props.current) && (props.current.width > width || props.current.height > height)

    const onLoadSuccess = useCallback((proxy: { numPages: number }) => {
        setNumPages(proxy.numPages)
    }, [])

    const x = useMotionValue(0)
    const y = useMotionValue(0)

    useEffect(() => setZoom(false), [props.current.id])

    return (props.current.preview_type === 'application/pdf' ?
        (isMobile ? <Document
                    className={clsx(props.className, styles.preview, styles.pdf)}
                    file={urlWithParams({filename: props.current.filename})(ROUTES.FILE_PREVIEW_PDF)}
                    onLoadSuccess={onLoadSuccess}
                >
                    <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false}/>
                    <Outline onItemClick={({pageNumber}) => setPageNumber(parseInt(pageNumber))}/>
                    <div className={styles.navigation}>Страница 1 из {numPages}</div>
                </Document> :
                <object
                    className={clsx(props.className, styles.preview, styles.file)}
                    data={urlWithParams({filename: props.current.filename})(ROUTES.FILE_PREVIEW_PDF)}
                    type={'application/pdf'}
                />
        ) : (
            zoom ? <div
                    ref={parentRef}
                    className={clsx(props.className, styles.preview, styles.zoom, {[styles.drag]: position})}
                    onMouseDown={event => setPosition({
                        x: event.clientX,
                        y: event.clientY,
                    })}
                    onClick={event => {
                        if(position?.x === event.clientX && position.y === event.clientY) {
                            setZoom(false)
                        }
                        setPosition(undefined)
                    }}
                >
                <motion.img
                    className={styles.original}
                    drag
                    dragConstraints={parentRef}
                    src={urlWithParams({filename: props.current.filename})(ROUTES.FILE_ORIGINAL)}
                    alt={props.current.name}
                    style={{x, y}}
                />
            </div> :
                <Image
                    ref={imageRef}
                    className={clsx(props.className, styles.preview, styles.image, {[styles.zoomIn]: canZoom})}
                    file={props.current}
                    objectFit={'scale-down'}
                    size={'high'}
                    onClick={event => {
                        if(canZoom && isImage(props.current)) {
                            const position = calculatePosition(event, props.current)
                            setZoom(true)
                            x.set(position.x)
                            y.set(position.y)
                        }
                    }}
                />
        )
    )
}

export default GalleryPreview